import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// The story bible: everything the writer and editor must keep true. It's read
// before every draft and every change. It fills itself (from your brief, from
// rules you say in the chat, and from what the writer notices), and you can
// edit anything in it. What the writer notices waits here for you to keep or skip.

const Icon = ({ children, size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const PencilIcon = (p) => <Icon {...p}><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></Icon>;
const XIcon = (p) => <Icon {...p}><path d="M6 6l12 12" /><path d="M18 6L6 18" /></Icon>;
const PlusIcon = (p) => <Icon {...p}><path d="M12 5v14" /><path d="M5 12h14" /></Icon>;
const SearchIcon = (p) => <Icon {...p}><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></Icon>;
const SparkIcon = (p) => <Icon {...p}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6" /></Icon>;
const PersonIcon = (p) => <Icon {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></Icon>;
const PinIcon = (p) => <Icon {...p}><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></Icon>;
const RuleIcon = (p) => <Icon {...p}><path d="M5 4h14v16H5z" /><path d="M9 9h6M9 13h6M9 17h3" /></Icon>;
const ClockIcon = (p) => <Icon {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>;
const QuillIcon = (p) => <Icon {...p}><path d="M20 4C11 4 6 10 5 20" /><path d="M20 4c0 7-5 11-11 11" /></Icon>;

const SECTIONS = [
  { key: 'characters', title: 'characters', icon: PersonIcon, grouped: true, addGroup: 'add a character', groupPh: 'their name', factPh: 'something that must stay true about them' },
  { key: 'world', title: 'places & things', icon: PinIcon, grouped: true, addGroup: 'add a place or thing', groupPh: 'what is it called', factPh: 'what must stay true about it' },
  { key: 'rules', title: 'rules', icon: RuleIcon, addLabel: 'add a rule', factPh: 'something it must always, or never, do' },
  { key: 'timeline', title: "what's happened", icon: ClockIcon, addLabel: 'add something that happened', factPh: 'something that happened', chapters: true },
  { key: 'style', title: 'style & voice', icon: QuillIcon, addLabel: 'add a style note', factPh: 'e.g. short sentences in fight scenes' },
];
const TITLES = Object.fromEntries(SECTIONS.map(x => [x.key, x.title]));

const sourceLine = (e) => {
  const where = e.chapter ? ` · ch. ${e.chapter}` : '';
  const base = e.source === 'brief' ? 'from your brief' : e.source === 'writer' ? `the writer noticed${where}` : e.chat ? `you said it in the chat${where}` : `you added it${where}`;
  return e.edited ? `${base} · edited` : base;
};

export default function WritingBible() {
  const navigate = useNavigate();
  const { workId } = useParams();
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);         // entry id, or 'sg:<id>', or 'name:<section>:<subject>'
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(null);           // '<section>' or '<section>:<subject>' or '<section>:+'
  const [addText, setAddText] = useState('');
  const [addName, setAddName] = useState('');
  const [addChapter, setAddChapter] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const sectionRefs = useRef({});

  const load = () => axios.get(`${API}/writing/works/${workId}/bible?token=${token}`)
    .then(res => { setData(res.data); setErrorMsg(''); })
    .catch(err => setErrorMsg(err?.response?.data?.detail || "couldn't load the bible"));
  useEffect(() => { if (!token) { navigate('/'); return; } load(); }, [workId]);

  const call = (req, after) => req.then(() => { after && after(); return load(); })
    .catch(err => setErrorMsg(err?.response?.data?.detail || "that didn't save — try again"));
  const base = `${API}/writing/works/${workId}`;

  const startEdit = (key, text) => { setEditing(key); setDraft(text); setAdding(null); };
  const stopEdit = () => { setEditing(null); setDraft(''); };
  const startAdd = (key) => { setAdding(key); setAddText(''); setAddName(''); setAddChapter(''); setEditing(null); };
  const stopAdd = () => { setAdding(null); setAddText(''); setAddName(''); setAddChapter(''); };

  const saveEntry = (e) => { if (!draft.trim()) return; call(axios.put(`${base}/bible/${e.id}`, { token, text: draft }), stopEdit); };
  const removeEntry = (e) => call(axios.delete(`${base}/bible/${e.id}?token=${token}`));
  const addEntry = (section, subject) => {
    if (!addText.trim() || (subject === '+' && !addName.trim())) return;
    call(axios.post(`${base}/bible`, { token, section, subject: subject === '+' ? addName : (subject || ''), text: addText, chapter: addChapter ? Number(addChapter) : null }), stopAdd);
  };
  const rename = (section, from) => { if (!draft.trim() || draft.trim() === from) { stopEdit(); return; } call(axios.post(`${base}/bible/rename`, { token, section, from, to: draft }), stopEdit); };
  const keepSuggestion = (sg, text) => call(axios.post(`${base}/bible-suggestions/${sg.id}/keep`, { token, text: text || sg.text }), stopEdit);
  const skipSuggestion = (sg) => call(axios.delete(`${base}/bible-suggestions/${sg.id}?token=${token}`));

  if (!data) {
    return (
      <div style={s.page}>
        <header style={s.header}><button style={s.back} onClick={() => navigate(-1)} aria-label="back">←</button></header>
        <main style={s.main}>{errorMsg ? <div style={s.error}>{errorMsg}</div> : <div style={s.muted}>opening…</div>}</main>
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const match = (e) => !q || e.text.toLowerCase().includes(q) || (e.subject || '').toLowerCase().includes(q);
  const entries = data.entries.filter(match);
  const series = data.kind === 'series';

  const keyDown = (ev, onSave, onCancel) => {
    if (ev.key === 'Escape') onCancel();
    if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey || !ev.shiftKey)) { ev.preventDefault(); onSave(); }
  };

  const entryRow = (e) => (
    <div key={e.id} style={s.entry}>
      {editing === e.id ? (
        <div style={{ flex: 1 }}>
          <textarea autoFocus className="wbb-in" style={s.editArea} rows={2} value={draft} onChange={ev => setDraft(ev.target.value)}
            onKeyDown={ev => keyDown(ev, () => saveEntry(e), stopEdit)} aria-label="edit" />
          <div style={s.editBtns}>
            <button className="wbb-btn" style={s.save} onClick={() => saveEntry(e)}>save</button>
            <button className="wbb-btn" style={s.cancel} onClick={stopEdit}>cancel</button>
          </div>
        </div>
      ) : (
        <>
          {e.section === 'timeline' && e.chapter ? <span style={s.chChip}>ch. {e.chapter}</span> : <span style={s.bullet} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={s.entryText}>{e.text}</div>
            <div style={s.entrySrc}>{sourceLine(e)}</div>
          </div>
          <button className="wbb-btn" style={s.iconBtn} onClick={() => startEdit(e.id, e.text)} aria-label="edit"><PencilIcon size={14} /></button>
          <button className="wbb-btn" style={s.iconBtn} onClick={() => removeEntry(e)} aria-label="remove"><XIcon size={14} /></button>
        </>
      )}
    </div>
  );

  const addBox = (section, subject, sec) => (
    <div style={s.addBox}>
      {subject === '+' && (
        <input autoFocus className="wbb-in" style={s.nameInput} value={addName} onChange={ev => setAddName(ev.target.value)} placeholder={sec.groupPh} aria-label={sec.groupPh} />
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        {sec.chapters && series && (
          <input className="wbb-in" style={s.chInput} inputMode="numeric" value={addChapter} onChange={ev => setAddChapter(ev.target.value.replace(/\D/g, '').slice(0, 3))} placeholder="ch." aria-label="chapter" />
        )}
        <textarea autoFocus={subject !== '+'} className="wbb-in" style={s.editArea} rows={2} value={addText} onChange={ev => setAddText(ev.target.value)}
          onKeyDown={ev => keyDown(ev, () => addEntry(section, subject), stopAdd)} placeholder={sec.factPh} aria-label={sec.factPh} />
      </div>
      <div style={s.editBtns}>
        <button className="wbb-btn" style={addText.trim() && (subject !== '+' || addName.trim()) ? s.save : s.saveOff} onClick={() => addEntry(section, subject)}>add</button>
        <button className="wbb-btn" style={s.cancel} onClick={stopAdd}>cancel</button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <style>{`
        .wbb-btn:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
        .wbb-in:focus { border-color: rgba(201,147,90,0.55) !important; outline: none; }
        .wbb-in::placeholder, .wbb-search input::placeholder { color: #6d5d4e; }
        .wbb-search:focus-within { border-color: rgba(201,147,90,0.45) !important; }
        .wbb-search input:focus { outline: none; }
      `}</style>

      <header style={s.header}>
        <button className="wbb-btn" style={s.back} onClick={() => navigate(-1)} aria-label="back">←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={s.title}>story bible</div>
          <div style={s.sub}>{data.title}</div>
        </div>
      </header>

      <main style={s.main}>
        <div style={s.intro}>
          the writer and editor read this before every draft and every change, and the editor checks the story against it.
          it fills itself from your brief, from rules you say in the chat, and from what the writer notices — and you can change anything here.
        </div>

        {errorMsg && <div style={s.error} onClick={() => setErrorMsg('')}>{errorMsg}</div>}

        <div className="wbb-search" style={s.searchWrap}>
          <SearchIcon />
          <input style={s.search} value={query} onChange={e => setQuery(e.target.value)} placeholder="find in the bible" aria-label="find in the bible" />
          {query && <button className="wbb-btn" style={s.iconBtn} onClick={() => setQuery('')} aria-label="clear"><XIcon size={13} /></button>}
        </div>

        <nav style={s.jump} aria-label="sections">
          {SECTIONS.map(sec => {
            const n = entries.filter(e => e.section === sec.key).length;
            return (
              <button key={sec.key} className="wbb-btn" style={s.jumpChip}
                onClick={() => sectionRefs.current[sec.key]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                {sec.key === 'timeline' && !series ? 'what happens' : sec.title} <span style={s.jumpN}>{n}</span>
              </button>
            );
          })}
        </nav>

        {data.suggestions.length > 0 && !q && (
          <section style={s.suggestCard}>
            <div style={s.suggestHead}><SparkIcon /> the writer noticed these — keep them?</div>
            {data.suggestions.map(sg => (
              <div key={sg.id} style={s.suggestRow}>
                <div style={s.suggestMeta}>{TITLES[sg.section]}{sg.subject ? ` · ${sg.subject}` : ''}{sg.chapter ? ` · from ch. ${sg.chapter}` : ''}</div>
                {editing === `sg:${sg.id}` ? (
                  <>
                    <textarea autoFocus className="wbb-in" style={s.editArea} rows={2} value={draft} onChange={ev => setDraft(ev.target.value)}
                      onKeyDown={ev => keyDown(ev, () => keepSuggestion(sg, draft), stopEdit)} aria-label="edit before keeping" />
                    <div style={s.editBtns}>
                      <button className="wbb-btn" style={s.save} onClick={() => keepSuggestion(sg, draft)}>keep</button>
                      <button className="wbb-btn" style={s.cancel} onClick={stopEdit}>cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={s.entryText}>{sg.text}</div>
                    <div style={s.editBtns}>
                      <button className="wbb-btn" style={s.save} onClick={() => keepSuggestion(sg)}>keep</button>
                      <button className="wbb-btn" style={s.cancel} onClick={() => startEdit(`sg:${sg.id}`, sg.text)}>edit first</button>
                      <button className="wbb-btn" style={s.cancel} onClick={() => skipSuggestion(sg)}>skip</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </section>
        )}

        {SECTIONS.map(sec => {
          const mine = entries.filter(e => e.section === sec.key);
          if (q && !mine.length) return null;
          const SecIcon = sec.icon;
          const title = sec.key === 'timeline' && !series ? 'what happens' : sec.title;
          const groups = sec.grouped ? [...new Set(mine.map(e => e.subject || '—'))] : [];
          const ordered = sec.key === 'timeline' ? [...mine].sort((a, b) => (a.chapter || 0) - (b.chapter || 0)) : mine;
          return (
            <section key={sec.key} ref={el => { sectionRefs.current[sec.key] = el; }} style={s.section}>
              <div style={s.secHead}><SecIcon size={16} /><span>{title}</span><span style={s.secN}>{mine.length}</span></div>

              {sec.grouped ? (
                <>
                  {groups.map(g => {
                    const facts = mine.filter(e => (e.subject || '—') === g);
                    const nameKey = `name:${sec.key}:${g}`;
                    return (
                      <div key={g} style={s.group}>
                        <div style={s.groupHead}>
                          {editing === nameKey ? (
                            <>
                              <input autoFocus className="wbb-in" style={s.nameInput} value={draft} onChange={ev => setDraft(ev.target.value)}
                                onKeyDown={ev => keyDown(ev, () => rename(sec.key, g), stopEdit)} aria-label="rename" />
                              <button className="wbb-btn" style={s.save} onClick={() => rename(sec.key, g)}>save</button>
                            </>
                          ) : (
                            <>
                              <span style={s.groupName}>{g}</span>
                              <button className="wbb-btn" style={s.iconBtn} onClick={() => startEdit(nameKey, g)} aria-label={`rename ${g}`}><PencilIcon size={13} /></button>
                            </>
                          )}
                        </div>
                        {facts.map(entryRow)}
                        {adding === `${sec.key}:${g}` ? addBox(sec.key, g === '—' ? '' : g, sec) : (
                          <button className="wbb-btn" style={s.addLink} onClick={() => startAdd(`${sec.key}:${g}`)}><PlusIcon size={13} /> add about {g}</button>
                        )}
                      </div>
                    );
                  })}
                  {!q && (adding === `${sec.key}:+` ? addBox(sec.key, '+', sec) : (
                    <button className="wbb-btn" style={s.addBig} onClick={() => startAdd(`${sec.key}:+`)}><PlusIcon size={14} /> {sec.addGroup}</button>
                  ))}
                </>
              ) : (
                <>
                  {ordered.map(entryRow)}
                  {!mine.length && !q && <div style={s.muted}>nothing here yet.</div>}
                  {!q && (adding === sec.key ? addBox(sec.key, '', sec) : (
                    <button className="wbb-btn" style={s.addBig} onClick={() => startAdd(sec.key)}><PlusIcon size={14} /> {sec.addLabel}</button>
                  ))}
                </>
              )}
            </section>
          );
        })}

        {q && !entries.length && <div style={s.muted}>nothing in the bible matches “{query}”.</div>}
      </main>
    </div>
  );
}

const serif = "'Cormorant Garamond', serif";
const sans = "'DM Sans', sans-serif";
const s = {
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: sans, fontWeight: 300 },
  header: { position: 'sticky', top: 0, zIndex: 10, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)' },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { fontFamily: serif, fontSize: '21px', fontStyle: 'italic' },
  sub: { fontSize: '11.5px', color: '#8a7466', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  main: { maxWidth: '720px', margin: '0 auto', padding: '22px 20px 90px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '14px' },
  intro: { fontSize: '13px', color: '#8a7466', lineHeight: 1.65 },
  muted: { fontSize: '12.5px', color: '#6d5d4e', padding: '4px 2px' },
  searchWrap: { display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 6px 4px 14px', borderRadius: '14px', background: '#1e1825', border: '1px solid rgba(201,147,90,0.12)', color: '#8a7466' },
  search: { flex: 1, minWidth: 0, background: 'transparent', border: 'none', padding: '9px 0', color: '#e8ddd0', fontSize: '14px', fontFamily: sans },
  jump: { display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' },
  jumpChip: { flexShrink: 0, padding: '6px 12px', borderRadius: '14px', border: '1px solid rgba(201,147,90,0.18)', background: 'transparent', color: '#c9a57a', fontSize: '12px', cursor: 'pointer', fontFamily: sans, whiteSpace: 'nowrap' },
  jumpN: { color: '#6d5d4e', marginLeft: '3px' },
  suggestCard: { padding: '16px', borderRadius: '18px', background: 'rgba(201,147,90,0.07)', border: '1px solid rgba(201,147,90,0.28)', display: 'flex', flexDirection: 'column', gap: '12px' },
  suggestHead: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#c9935a' },
  suggestRow: { padding: '12px', borderRadius: '14px', background: '#1a1520', display: 'flex', flexDirection: 'column', gap: '6px' },
  suggestMeta: { fontSize: '10.5px', color: '#8a7466', letterSpacing: '0.08em', textTransform: 'uppercase' },
  section: { scrollMarginTop: '80px', background: '#1e1825', border: '1px solid rgba(201,147,90,0.08)', borderRadius: '20px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  secHead: { display: 'flex', alignItems: 'center', gap: '9px', fontFamily: serif, fontSize: '21px', fontStyle: 'italic', color: '#e8ddd0', marginBottom: '4px' },
  secN: { fontFamily: sans, fontStyle: 'normal', fontSize: '11px', color: '#6d5d4e', marginLeft: 'auto' },
  group: { padding: '12px', borderRadius: '14px', background: '#17131b', display: 'flex', flexDirection: 'column', gap: '4px' },
  groupHead: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' },
  groupName: { fontFamily: serif, fontSize: '19px', color: '#e8c9a4' },
  entry: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '7px 2px' },
  bullet: { width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(201,147,90,0.5)', marginTop: '8px', flexShrink: 0 },
  chChip: { flexShrink: 0, padding: '2px 7px', borderRadius: '8px', background: 'rgba(201,147,90,0.14)', color: '#c9935a', fontSize: '11px', marginTop: '1px' },
  entryText: { fontSize: '14px', color: '#e0d4c6', lineHeight: 1.5 },
  entrySrc: { fontSize: '10.5px', color: '#65574b', marginTop: '2px' },
  iconBtn: { width: '28px', height: '28px', borderRadius: '50%', border: 'none', background: 'transparent', color: '#6d5d4e', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  editArea: { width: '100%', boxSizing: 'border-box', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.2)', borderRadius: '10px', padding: '9px 11px', color: '#e8ddd0', fontSize: '14px', lineHeight: 1.5, fontFamily: sans, resize: 'vertical' },
  nameInput: { flex: 1, minWidth: 0, boxSizing: 'border-box', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.2)', borderRadius: '10px', padding: '8px 11px', color: '#e8c9a4', fontSize: '16px', fontFamily: serif },
  chInput: { width: '52px', flexShrink: 0, boxSizing: 'border-box', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.2)', borderRadius: '10px', padding: '9px 8px', color: '#c9935a', fontSize: '13px', fontFamily: sans, textAlign: 'center' },
  editBtns: { display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' },
  save: { padding: '6px 14px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans },
  saveOff: { padding: '6px 14px', borderRadius: '10px', border: 'none', background: 'rgba(201,147,90,0.12)', color: 'rgba(201,147,90,0.4)', fontSize: '12.5px', fontFamily: sans },
  cancel: { padding: '6px 12px', borderRadius: '10px', border: '1px solid rgba(201,147,90,0.18)', background: 'transparent', color: '#a8927f', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans },
  addBox: { display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 0 2px' },
  addLink: { alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 2px', border: 'none', background: 'transparent', color: '#a8865e', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans },
  addBig: { alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '12px', border: '1px dashed rgba(201,147,90,0.28)', background: 'transparent', color: '#c9935a', fontSize: '12.5px', cursor: 'pointer', fontFamily: sans, marginTop: '4px' },
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0', cursor: 'pointer' },
};
